import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom'
import { ReactNode, Suspense, lazy, useEffect, useState } from 'react'
import { useAbility } from '@casl/react'
import { App as AntApp } from 'antd'
import { BaseLayout } from './shared/layouts/base-layout'
import { NotFoundPage } from './pages/not-found'
import { LoadScreen } from './pages/load-screen'
import { Login } from './features/login'
import { useUserInfoStore } from './store'
import { paths } from './shared'
import { UserInfoResponse } from './types/user'
import { ErrorBoundary } from './error-boundary'
import { AbilityContext, Action, defineAbilityFor, Subjects, updateAbility } from '@/app/auth'

const Dashboard = lazy(() => import(/* webpackChunkName: "dashboard", webpackPreload: true */ './features/dashboard'))

const ResultPurchasePage = lazy(
  () => import(/* webpackChunkName: "result-purchase", webpackPreload: true */ './features/result-purchase-page')
)

const UsersPage = lazy(() => import(/* webpackChunkName: "users", webpackPreload: true */ './features/admin-users'))

const SingleUserPage = lazy(() => import(/* webpackChunkName: "users", webpackPreload: true */ './features/admin-user'))

const XeroCallbackPage = lazy(
  () => import(/* webpackChunkName: "xero-callback", webpackPreload: true */ './features/xero-callback')
)

const ProtectedRoute = ({ element, action, resource }: { element: ReactNode; action: Action; resource: Subjects }) => {
  const { fetch, info } = useUserInfoStore((state) => state)
  const [userIsLoaded, isLoaded] = useState(false)
  const ability = useAbility(AbilityContext)
  const canAccess = ability.can(action, resource)

  useEffect(() => {
    if (!userIsLoaded) {
      const fetchUser = async () => {
        const user = await fetch()
        if (user) updateAbility(ability, user)
        isLoaded(true)
      }

      fetchUser()
    }
  }, [userIsLoaded])

  if (!userIsLoaded) {
    return <LoadScreen />
  }

  if (!canAccess) return <RedirectToUnauthorized role={info.role} />

  return element
}

const RedirectToUnauthorized = ({ role }: { role: UserInfoResponse['role'] }) => {
  const ability = useAbility(AbilityContext)

  if (!role) {
    return <Navigate to={paths.login()} />
  }

  let to = ''

  if (ability.can(Action.Read, 'dashboard')) {
    to = paths.main()
  } else if (ability.can(Action.Read, 'users')) {
    to = paths.users()
  } else {
    to = paths.login()
  }

  return <Navigate to={to} />
}

const router = createBrowserRouter([
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/',
    errorElement: <ErrorBoundary />,
    element: <BaseLayout />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<LoadScreen />}>
            <ProtectedRoute element={<Dashboard />} action={Action.Read} resource='dashboard' />
          </Suspense>
        ),
      },
      {
        path: '/xero-callback',
        element: <ProtectedRoute element={<XeroCallbackPage />} action={Action.Read} resource='xero-callback' />,
      },
      {
        path: '/users',
        children: [
          {
            index: true,
            element: <ProtectedRoute element={<UsersPage />} action={Action.Read} resource='users' />,
          },
          {
            path: ':id',
            element: <ProtectedRoute element={<SingleUserPage />} action={Action.Read} resource='users' />,
          },
        ],
      },
      {
        path: '/success',
        element: <ProtectedRoute element={<ResultPurchasePage />} action={Action.Read} resource='result-purchase' />,
      },
      {
        path: '/fail',
        element: <ProtectedRoute element={<ResultPurchasePage />} action={Action.Read} resource='result-purchase' />,
      },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
])

export default function App() {
  const ability = defineAbilityFor()

  return (
    <AntApp>
      <AbilityContext.Provider value={ability}>
        <Suspense fallback={<LoadScreen />}>
          <RouterProvider router={router} />
        </Suspense>
      </AbilityContext.Provider>
    </AntApp>
  )
}
